/**
 * Designer-invoice payment method choice + phase gates (client mirror of
 * functions/src/designerInvoicePayment.ts).
 *
 * Default: estimating/precon/overhead under $2,000 → project_card; else ach_office.
 * Bill Payment (`ach_office`) always creates supplierBills + QBO bill on final
 * approval (when writes enabled), whatever the line phases are. Card methods
 * stay PO-only.
 */

export const DESIGNER_INVOICE_PAYMENT_METHOD_IDS = [
  'project_card',
  'company_card',
  'personal_card',
  'ach_office',
] as const;

export type DesignerInvoicePaymentMethodId =
  (typeof DESIGNER_INVOICE_PAYMENT_METHOD_IDS)[number];

export const DESIGNER_INVOICE_CARD_PAYMENT_METHOD_IDS = [
  'project_card',
  'company_card',
  'personal_card',
] as const;

export type DesignerInvoiceCardPaymentMethodId =
  (typeof DESIGNER_INVOICE_CARD_PAYMENT_METHOD_IDS)[number];

export const DESIGNER_INVOICE_CARD_DEFAULT_PHASES = [
  'estimating',
  'precon',
  'overhead',
] as const;

export const DESIGNER_INVOICE_CARD_DEFAULT_MAX_AMOUNT = 2000;

export const DESIGNER_INVOICE_PAYMENT_METHOD_LABELS: Record<
  DesignerInvoicePaymentMethodId,
  string
> = {
  project_card: 'Project Card',
  company_card: 'Company Card',
  personal_card: 'Personal card',
  ach_office: 'Bill Payment',
};

export const DESIGNER_INVOICE_PAYMENT_METHOD_OPTIONS: Array<{
  id: DesignerInvoicePaymentMethodId;
  label: string;
}> = DESIGNER_INVOICE_PAYMENT_METHOD_IDS.map((id) => ({
  id,
  label: DESIGNER_INVOICE_PAYMENT_METHOD_LABELS[id],
}));

export const DESIGNER_INVOICE_PAYMENT_METHOD_DEFAULT_NOTE =
  'Default: estimating/precon/overhead under $2,000 → Project Card; otherwise Bill Payment (ACH).';

export function isDesignerInvoicePaymentMethodId(
  value: unknown,
): value is DesignerInvoicePaymentMethodId {
  return (
    typeof value === 'string'
    && (DESIGNER_INVOICE_PAYMENT_METHOD_IDS as readonly string[]).includes(value)
  );
}

export function isDesignerInvoiceCardPaymentMethodId(
  value: unknown,
): value is DesignerInvoiceCardPaymentMethodId {
  return (
    typeof value === 'string'
    && (DESIGNER_INVOICE_CARD_PAYMENT_METHOD_IDS as readonly string[]).includes(value)
  );
}

export function designerInvoicePaymentMethodLabel(
  id: DesignerInvoicePaymentMethodId | string | null | undefined,
): string {
  const key = String(id || '').trim();
  if (isDesignerInvoicePaymentMethodId(key)) {
    return DESIGNER_INVOICE_PAYMENT_METHOD_LABELS[key];
  }
  return key || DESIGNER_INVOICE_PAYMENT_METHOD_LABELS.ach_office;
}

export function parseDesignerInvoicePaymentMethodId(
  raw: unknown,
):
  | { ok: true; paymentMethodId: DesignerInvoicePaymentMethodId | null }
  | { ok: false; error: string; code: 'invalid_payment_method' } {
  if (raw == null || raw === '') {
    return { ok: true, paymentMethodId: null };
  }
  const value = String(raw).trim();
  if (!value) return { ok: true, paymentMethodId: null };
  if (!isDesignerInvoicePaymentMethodId(value)) {
    return {
      ok: false,
      error: `paymentMethodId must be one of: ${DESIGNER_INVOICE_PAYMENT_METHOD_IDS.join(', ')}`,
      code: 'invalid_payment_method',
    };
  }
  return { ok: true, paymentMethodId: value };
}

export function defaultDesignerInvoicePaymentMethodId(input: {
  phase: string | null | undefined;
  amount: number;
}): DesignerInvoicePaymentMethodId {
  const phase = String(input.phase || '').trim().toLowerCase();
  const amount = Number(input.amount);
  const softPhase = (DESIGNER_INVOICE_CARD_DEFAULT_PHASES as readonly string[]).includes(phase);
  if (softPhase && Number.isFinite(amount) && amount < DESIGNER_INVOICE_CARD_DEFAULT_MAX_AMOUNT) {
    return 'project_card';
  }
  return 'ach_office';
}

export function resolveDesignerInvoicePaymentMethodId(input: {
  paymentMethodId?: unknown;
  phase?: string | null;
  amount?: number;
}): DesignerInvoicePaymentMethodId {
  const parsed = parseDesignerInvoicePaymentMethodId(input.paymentMethodId);
  if (parsed.ok && parsed.paymentMethodId) return parsed.paymentMethodId;
  return defaultDesignerInvoicePaymentMethodId({
    phase: input.phase,
    amount: Number(input.amount) || 0,
  });
}

/**
 * True when every non-overhead phase is under_contract.
 * Kept for diagnostics / UI; Bill Payment no longer requires this for payables.
 */
export function phasesAreUnderContractOnly(
  phases: Array<string | null | undefined>,
): boolean {
  const normalized = phases
    .map((p) => String(p || '').trim().toLowerCase())
    .filter((p) => p && p !== 'overhead');
  if (!normalized.length) return false;
  return normalized.every((p) => p === 'under_contract');
}

/**
 * Billing & Payables supplierBills: Bill Payment (`ach_office`) only.
 * Phase does not gate. `phases` kept for call-site compatibility.
 */
export function shouldCreateDesignerInvoiceSupplierBill(input: {
  paymentMethodId: string | null | undefined;
  phases?: Array<string | null | undefined>;
}): boolean {
  void input.phases;
  return String(input.paymentMethodId || '').trim() === 'ach_office';
}

/**
 * QuickBooks vendor bill: Bill Payment only (same gate as supplierBills).
 * Card methods never create a QuickBooks bill on this path.
 */
export function shouldCreateDesignerInvoiceQboBill(input: {
  paymentMethodId: string | null | undefined;
  phases?: Array<string | null | undefined>;
}): boolean {
  if (isDesignerInvoiceCardPaymentMethodId(input.paymentMethodId)) return false;
  return shouldCreateDesignerInvoiceSupplierBill(input);
}

export function isDesignerInvoiceAgentPayableAmount(amount: number): boolean {
  return Number.isFinite(amount) && amount > 0 && amount < DESIGNER_INVOICE_CARD_DEFAULT_MAX_AMOUNT;
}

export function primaryDesignerInvoicePhase(
  lines: Array<{ phase?: string | null }>,
  reviewPhase?: string | null,
): string {
  const fromReview = String(reviewPhase || '').trim();
  if (fromReview) return fromReview;
  const job = lines.find((l) => String(l.phase || '').trim() && l.phase !== 'overhead');
  if (job?.phase) return String(job.phase);
  return String(lines[0]?.phase || 'estimating');
}
